<template>
  <content-subpage
    class="game-award-content-subpage bulk-award-preview"
    :class="cssClasses"
    :has-parent-subpage="hasParentSubpage"
    :visible="visible"
    :titles="gameAwardExist ? titles : [gameAward.title]"
    :show-content-title="false"
    :back-label="backLabel"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <template #main-content>
      <overlay-loader v-if="isLoading" />
      <template v-else>
        <cv-button
          v-if="showUseAsGameAvatarButton"
          kind="tertiary"
          size="field"
          @click="handleSelectAvatar"
        >
          <span>{{ t("gamification_game_avatar_preview_use_button_label") }}</span>
        </cv-button>

        <cv-inline-notification
          v-if="gameAwardExist && popupViewable"
          kind="info"
          :title="bannerTitle"
          low-contrast
          hide-close-button
        >
          <template #subtitle>
            <div>{{ t("gamification_bulk_award_preview_subpage_notification_message") }}</div>
          </template>
        </cv-inline-notification>

        <div v-if="!gameAwardExist" class="badge-error-state">
          <DigitalBadgeEmpty />
          <p>{{ t("profile_subpage_digital_badges_something_went_wrong_text") }}</p>
        </div>
        <div v-else class="award-preview">
          <div class="wrapper">
            <div v-if="backgroundUrl" class="overlay" />
            <div v-if="backgroundUrl" class="theme-placeholder">
              <img :src="backgroundUrl" class="theme-image" />
            </div>

            <div v-if="!isHideImage" class="achievement-icon-wrapper">
              <div class="image-placeholder">
                <img
                  v-if="gameAward.featuredImageUrl"
                  :src="gameAward.featuredImageUrl"
                  class="thumbnail"
                />
                <AchievementAvatar v-else-if="isGameAvatar" class="collectible" />
                <Achievement v-else-if="isAchievement" class="collectible" />
                <DigitalBadge110 v-else-if="isDigitalBadge" class="collectible digital-badge" />
                <GameStoryThumbnail v-else class="game-story" />
              </div>
            </div>
            <div v-show="isCollectible && !isHideImage" class="radial-wrapper">
              <svg ref="svgEl" class="radial" viewBox="0 0 400 400">
                <defs>
                  <linearGradient id="triangleGradient">
                    <stop offset="20%" stop-color="#FFF" stop-opacity="0"></stop>
                    <stop offset="100%" stop-color="#FFF" stop-opacity="0.8"></stop>
                  </linearGradient>
                </defs>
                <g v-for="i in 10" :key="i" class="triangle-wrapper">
                  <svg class="triangle" fill="url(#triangleGradient)">
                    <polygon points="50,30 200,200, 0,80"></polygon>
                  </svg>
                </g>
              </svg>
            </div>

            <div class="message-box">
              <div v-if="isDigitalBadge" class="digital-badge-header">
                <DigitalBadgeIndicator class="digital-badge-indicator" />
                <div class="tooltip-wrapper">
                  <cv-tooltip
                    direction="bottom"
                    alignment="center"
                    :tip="t('gamification_award_modal_digital_badge_indicator_tooltip')"
                  >
                    <svg-icon name="Info16" kind="indicative" />
                  </cv-tooltip>
                </div>
              </div>
              <div class="title">
                {{ gameAward.title }}
              </div>
              <div class="description">
                <rich-text :content="gameAward.description" :expandable="false" :truncate="false" />
              </div>
              <div v-if="showModuleLink" class="action">
                <cv-link @click="handleOpenLesson">
                  {{ gameAward.lessonTitle }}
                  <span class="icon">
                    <svg-icon name="ExternalLink16" kind="action" />
                  </span>
                </cv-link>
              </div>
            </div>

            <div v-if="hasChoice" class="choice-wrapper">
              <div v-if="showDropDownOrTextChoices" class="choice-dropdown">
                <text-input
                  v-if="isTextInputChoice"
                  v-model:value="selectedChoice"
                  input-type="text"
                  :placeholder="t('game_award_modal_answer_placeholder')"
                />
                <dropdown-component v-else kind="white">
                  <cv-dropdown-item :selected="true" :value="selectedChoice">
                    {{ selectedChoice }}
                  </cv-dropdown-item>
                </dropdown-component>
                <cv-button>
                  {{ t("next_button_label") }}
                </cv-button>
              </div>
              <div v-else>
                <cv-button
                  v-for="(choice, index) in gameChoiceList"
                  :key="index"
                  :kind="choiceButtonKind(choice)"
                >
                  {{ choice.choices }}
                </cv-button>
              </div>
            </div>
            <cv-button v-else-if="popupViewable">
              {{ t("next_button_label") }}
            </cv-button>
          </div>
        </div>
      </template>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import GameApi from "@apis/gamification.api";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";
import { GAME_AWARD_TYPE } from "@modules/attempt/service/gamification.constant";
import { debounceFn } from "@/utils/debounce";
import Achievement from "@images/game/Achievement.svg?component";
import GameStoryThumbnail from "@images/game/GameStoryThumbnail(lg).svg?component";
import AchievementAvatar from "@images/game/AchievementAvatar.svg?component";
import DigitalBadge110 from "@images/game/DigitalBadge110.svg?component";
import DigitalBadgeEmpty from "@images/placeholder/empty-states/DigitalBadgeEmpty.svg?component";
import DigitalBadgeIndicator from "@images/game/DigitalBadgeIndicator.svg?component";
import GamificationAwardViewMixin from "@modules/attempt/mixin/gamification.award.view.mixin";
import { isEmpty } from "@utils/var.check";
import BackgroundAudioPlayerMixin from "@modules/editor/mixin/background.audio.player.mixin";
import { ref, computed, onMounted, onBeforeUnmount, useTemplateRef } from "vue";
import { useToast } from "@/plugins/toast/composables/toast-instance";
import { useVm } from "@utils/vm.ts";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useContentSubpage } from "@/plugins/content-subpage/composables/content-subpage";
import { useRouter } from "vue-router";
import { useErrorHandler } from "@/plugins/common/composables/error";
import { AVATAR_DEFAULT_SVG_FILENAME } from "@modules/user-avatar/service/user.avatar.constant.ts";

// Props
const props = defineProps({
  params: { type: Object, default: null },
});

const svgElRef = useTemplateRef("svgEl");

// Computed
const gameAward = computed(() => {
  return props.params.gameAward;
});

const choice = computed(() => {
  return props.params.choice;
});

const backgroundUrl = computed(() => {
  return props.params.backgroundUrl;
});

const backLabel = computed(() => {
  return `${t("content_subpage_go_back_link")} ${parentSubpageName.value}`;
});

const bannerTitle = computed(() => {
  return props.params.studentPreview
    ? ""
    : t("gamification_bulk_award_preview_subpage_notification_title");
});

const popupViewable = computed(() => {
  return (
    gameAward.value.type === GAME_AWARD_TYPE.GAME_STORY || gameAward.value.popupViewableAfterReward
  );
});

const showUseAsGameAvatarButton = computed(() => {
  return isStudent.value && vm.isGameAvatar;
});

const showModuleLink = computed(() => {
  return props.params.showModuleLink === true;
});

const selectedChoice = computed(() => {
  if (!isEmpty(choice.value.answerText)) {
    return choice.value.answerText;
  } else {
    return choice.value.choices;
  }
});

const cssClasses = computed(() => {
  return {
    achievement: vm.gameAwardExist ? vm.isCollectible : false,
    "is-game-choice": vm.hasChoice,
    "is-error-state": !vm.gameAwardExist,
    "is-digital-badge": vm.isDigitalBadge,
  };
});

// Data
const visible = ref(true);
const isLoading = ref(false);
const parentSubpageName = ref("");
const { t } = useI18n();
const store = useStore();
const { titles, hasParentSubpage, actionHidden, actionBack, defaultExpose } = useContentSubpage(
  props.params,
);
const toastHandler = useToast();
const { vm, vmExpose } = useVm();
const router = useRouter();
const { passiveApiFailHandler } = useErrorHandler();
const emit = defineEmits(["reRenderAvatar"]);

const isStudent = computed(() => store.getters[`${NAMESPACE.AUTH}/isStudent`]);
const getUserGameAvatar = computed(() => store.getters[`${NAMESPACE.ATTEMPT}/getUserGameAvatar`]);
const attemptContext = computed(() => store.state[NAMESPACE.ATTEMPT].attemptContext);

defineOptions({
  mixins: [BackgroundAudioPlayerMixin, GamificationAwardViewMixin],
});

const setUserGameAvatar = (payload) => {
  return store.dispatch(`${NAMESPACE.ATTEMPT}/setUserGameAvatar`, payload);
};
// Methods
const setCSSVar = function () {
  // FIXME VUE3
  if (vm.gameAwardExist) {
    DOMUtils.setCSSVariableInElement(vm.$el, "--svg-scale", svgElRef.value.currentScale);
  }
};

const handleSelectAvatar = function () {
  const isSameAvatar = gameAward.value.image
    ? // if the game award avatar has image, check against the image
      getUserGameAvatar.value === gameAward.value.image
    : // else, check against the default achievement avatar
      vm.isGameAvatar &&
      (getUserGameAvatar.value === AVATAR_DEFAULT_SVG_FILENAME ||
        getUserGameAvatar.value?.startsWith("default"));
  if (isSameAvatar) {
    // no update is required
    showSuccessfulUpdateToast();
    return;
  }
  GameApi.selectGameAvatar({
    gameBookId: props.params.gameBookId,
    gameAwardId: gameAward.value.id,
    attemptableUuid: attemptContext.value.attemptableUuid,
    attemptableType: attemptContext.value.attemptableType,
  }).then(
    (response) => {
      if (response.code === "ok") {
        showSuccessfulUpdateToast();
      }
      if (gameAward.value.image) {
        setUserGameAvatar(gameAward.value.image);
      } else {
        setUserGameAvatar(AVATAR_DEFAULT_SVG_FILENAME);
      }
      emit("reRenderAvatar", gameAward.value.image);
    },
    (error) => {
      passiveApiFailHandler(error);
    },
  );
};

const showSuccessfulUpdateToast = function () {
  toastHandler.show("success", {
    title: t("gamification_game_avatar_updated_message"),
  });
};

const choiceButtonKind = function (choice) {
  return choice.id === choice.value.id ? "primary" : "tertiary";
};

const handleOpenLesson = function () {
  if (isStudent.value) {
    // TODO: SLS-11043 (DONE), attempt
    const routeData = router.resolve({
      name: "SelfStudyAttemptCover",
      params: {
        lessonUuid: gameAward.value.moeLibraryUuid,
      },
      query: {
        version: gameAward.value.lessonUuid,
      },
    });
    window.open(routeData.href, "_blank");
  } else {
    //isTeacher
    // TODO: SLS-11043 (OK), editor
    const routeData = router.resolve({
      name: "EditorCover",
      params: {
        resourceDomain: "moe-library",
        mode: "view",
        lessonUuid: gameAward.value.moeLibraryUuid,
      },
      query: {
        version: gameAward.value.lessonUuid,
      },
    });
    window.open(routeData.href, "_blank");
  }
};

// Mounted
onMounted(() => {
  setCSSVar();
  window.onresize = debounceFn(() => setCSSVar(), 10);
  vm.setAudio(props.params.audio);
  // Created
  if (hasParentSubpage) {
    parentSubpageName.value = titles.value.slice(-1)[0];
  }
  // to account for digital badges when it's null passing in
  if (vm.gameAwardExist) {
    titles.value.push(gameAward.value.title);
    vm.gameChoiceList = gameAward.value.gameChoiceList;
  }
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", setCSSVar);
});
defineExpose(defaultExpose);
vmExpose({
  gameAward,
});
</script>
<style lang="scss">
.game-award-content-subpage {
  .main {
    > .bx--btn {
      position: absolute;
      top: 1rem;
      right: 1rem;
    }
  }
}
</style>
